/**
 * The Companion shell (0.14): toolbar above every page, bottom bar on phones.
 *
 *   toolbar  [ Find …           ]   (● UK · data to 4 Sep)  [+ New ▾]
 *   phone    Dashboard · Find · (+) Log · Pipeline · More
 *
 * The as-of chip is the plan's "per-entity as-of chip": how old the ERP numbers on
 * screen are, from `/api/sales/me/scope` (freshness measured from the data, never
 * from a sync log). It follows the entity the page is showing (`sales:entity`).
 * "+ New" is only offered to people who can write (viewers see no button — the
 * server refuses them anyway).
 */
import { escapeHtml, getIcon, router, showModal, store } from '@isg/ui-kit';
import { salesApi } from '../services/sales-api.js';
import { canWrite, writesHiddenByActingAs } from '../services/sales-role.js';
import { getActingAs, stopActingAs } from '../company-switcher.js';
import { shortDate } from '../lib/fig.js';
import { createFind, openFind } from './find.js';
import { accountPicker, openLogSheet, openTaskSheet } from './log-sheet.js';
import { openProspectSheet } from './prospect-sheet.js';
import { openRequestSheet } from './request-sheet.js';
import { openWorkSheet } from './work-sheet.js';
import { getSavedFilterParams } from './sales-filters.js';
import { knownEntity, rememberHomeEntity } from '../lib/home-entity.js';

const ENTITY = { uk: 'UK', us: 'US', de: 'DE' };
const SOURCE_LABEL = { 'erp.commercial': 'Quotes, orders, invoices', 'erp.stock': 'Stock', engagement: 'Web & email' };
const freshnessCache = {};

/** The account the current page is about, if any (for + New prefills). */
function currentAccount() {
  const m = window.location.pathname.match(/^\/a\/([a-z]{2})\/([^/?#]+)/);
  if (!m) return null;
  const name = document.querySelector('[data-account-name]')?.dataset.accountName;
  return { entity: m[1], code: decodeURIComponent(m[2]), name: name || decodeURIComponent(m[2]) };
}

async function freshness(entity) {
  const hit = freshnessCache[entity];
  if (hit && Date.now() - hit.at < 5 * 60 * 1000) return hit.value;
  const res = await salesApi.getMyScope(entity ? { entity } : {});
  const value = { entity: (res.data || {}).entity, sources: (res.meta || {}).sources || {} };
  // asked with NO entity, the server answers with the person's HOME entity — remember it
  // for this tab, so links and + New never guess (lib/home-entity.js, sweep R2 #48)
  if (!entity) rememberHomeEntity(value.entity);
  freshnessCache[value.entity] = { at: Date.now(), value };
  return value;
}

function mountAsOf(el) {
  let shown = null;
  async function show(entity) {
    if (entity && entity === shown) return;
    try {
      const { entity: e, sources } = await freshness(entity);
      shown = e;
      const c = sources['erp.commercial'] || sources.erp || {};
      el.dataset.status = c.status || 'unavailable';
      el.innerHTML = `<span class="sc-asof-dot" aria-hidden="true"></span>
        <span>${escapeHtml(ENTITY[e] || e || '')} · ${c.as_of ? `data to ${escapeHtml(shortDate(c.as_of))}` : 'data age unknown'}</span>`;
      el.title = Object.entries(sources).map(([k, s]) =>
        `${SOURCE_LABEL[k] || k}: ${s.status}${s.as_of ? ` (to ${shortDate(s.as_of)})` : ''}${s.reason ? ` — ${s.reason}` : ''}`).join('\n');
      el.hidden = false;
    } catch (err) {
      el.hidden = true;
    }
  }
  window.addEventListener('sales:entity', (ev) => show(ev.detail));
  show(getSavedFilterParams().entity);
}

// The + New menu: a title and, UNDER it, what it is for (Callum, 2026-10-09: "the description
// under the item title" — it had been a dash-suffix on half the items and nothing on the rest).
const NEW_ITEMS = [
  ['log', 'phone', 'Log a call, visit or note', 'On the account’s timeline'],
  ['task', 'check-square', 'Task', 'Something to do, with a due date'],
  ['opportunity', 'target', 'Opportunity', 'One deal with one account'],
  ['project', 'briefcase', 'Project', 'A programme holding several deals'],
  ['quote', 'tag', 'Quote', 'Priced by isg-pricing, sent from here'],
  ['request', 'mail', 'Quote request', 'An RFQ that came by email or phone'],
  ['prospect', 'user-plus', 'Prospect', 'A company QX does not know yet'],
  ['lead', 'clipboard-list', 'Lead', 'A contact met at a show or visit'],
];
const newItem = ([id, icon, title, sub]) => `
      <button type="button" class="dropdown-item sc-new-item" role="menuitem" data-new="${id}">
        <span class="sc-new-icon">${getIcon(icon, 16)}</span>
        <span class="sc-new-text"><span class="sc-new-title">${title}</span><span class="sc-new-sub">${sub}</span></span>
      </button>`;

function newMenu() {
  const wrap = document.createElement('div');
  wrap.className = 'dropdown sc-new';
  wrap.innerHTML = `
    <button type="button" class="btn btn-primary btn-sm" aria-haspopup="menu" aria-expanded="false">${getIcon('plus', 14)} New</button>
    <div class="dropdown-menu sc-new-menu" role="menu">
      <div class="sc-new-head">Record</div>
      ${NEW_ITEMS.slice(0, 4).map(newItem).join('')}
      <div class="dropdown-divider"></div>
      <div class="sc-new-head">Quotes &amp; leads</div>
      ${NEW_ITEMS.slice(4).map(newItem).join('')}
    </div>`;
  const btn = wrap.querySelector('button');
  const menu = wrap.querySelector('.dropdown-menu');
  const setOpen = (on) => { menu.classList.toggle('active', on); btn.setAttribute('aria-expanded', String(on)); };
  btn.addEventListener('click', (e) => { e.stopPropagation(); setOpen(!menu.classList.contains('active')); });
  document.addEventListener('click', () => setOpen(false));
  menu.addEventListener('click', (e) => {
    const item = e.target.closest('[data-new]');
    if (!item) return;
    setOpen(false);
    // Acting as someone is read-only; say so instead of opening a sheet that cannot save.
    const acting = getActingAs();
    if (acting) {
      if (window.confirm(`You are acting as ${acting.name}, which is read-only. Stop acting as them and do this as yourself?`)) stopActingAs();
      return;
    }
    if (item.dataset.new === 'log') openLogSheet(currentAccount());
    else if (item.dataset.new === 'task') openTaskSheet(currentAccount());
    else if (item.dataset.new === 'opportunity') openWorkSheet('opportunity', currentAccount());
    else if (item.dataset.new === 'project') openWorkSheet('project', currentAccount());
    else if (item.dataset.new === 'quote') newQuote();
    else if (item.dataset.new === 'request') openRequestSheet(currentAccount());
    // no entity known → none sent, and the server binds the person's home entity (sweep R2 #48)
    else if (item.dataset.new === 'prospect') openProspectSheet({ entity: knownEntity() || undefined });
    else router.navigate('/new-lead');
  });
  return wrap;
}

/**
 * Start a quote from anywhere.
 *
 * 🔴 The quote studio was reachable ONLY by opening an account first, so a rep who
 * thought "I need to quote Acme" had to remember to go and find Acme before the feature
 * existed at all. A quote needs an account — pricing resolves the Company, the
 * catalogue and the price basis from it — so when we are not on one, ask, using the
 * same account picker the log sheet uses rather than a second kind of search.
 */
async function newQuote() {
  const { openQuoteSheet } = await import('./quote-sheet.js');
  const account = currentAccount();
  if (account) { openQuoteSheet(account); return; }

  const content = document.createElement('div');
  content.innerHTML = `
    <p class="sc-sheet-hint" style="margin-top:0">A quote is priced on the account's own
      Company and catalogue, so pick who it is for.</p>
    <div class="form-group"><label class="form-label">Account</label><div data-account></div></div>`;
  const modal = showModal({
    title: 'New quote', content, size: 'md',
    actions: [{ label: 'Cancel', className: 'btn btn-secondary' }],
  });
  accountPicker(content.querySelector('[data-account]'), null, (picked) => {
    if (!picked) return;
    modal.close();
    openQuoteSheet(picked);
  });
}

export function renderToolbar() {
  const toolbar = document.createElement('div');
  toolbar.className = 'global-toolbar';
  const left = document.createElement('div');
  left.className = 'global-toolbar-left';
  left.appendChild(createFind());
  const right = document.createElement('div');
  right.className = 'global-toolbar-right sc-toolbar-actions';
  const asOf = document.createElement('span');
  asOf.className = 'sc-asof';
  asOf.hidden = true;
  right.appendChild(asOf);
  // Feedback: everyone with Sales access, viewers too — it goes to the intranet's tracker
  const fb = document.createElement('button');
  fb.type = 'button'; fb.className = 'btn btn-ghost btn-sm sc-feedback'; fb.title = 'Send feedback — a problem, an idea or a question';
  fb.innerHTML = `${getIcon('message-square', 14)} <span>Feedback</span>`;
  fb.addEventListener('click', () => import('./feedback-sheet.js').then(m => m.openFeedbackSheet()));
  right.appendChild(fb);
  if (canWrite(store.get('salesRole')) || writesHiddenByActingAs(store, getActingAs())) right.appendChild(newMenu());
  toolbar.append(left, right);
  mountAsOf(asOf);
  return toolbar;
}

export function renderBottomBar() {
  const bar = document.createElement('nav');
  bar.className = 'sc-bottom-bar';
  bar.setAttribute('aria-label', 'Quick actions');
  const writer = canWrite(store.get('salesRole'));
  bar.innerHTML = `
    <a href="/" data-link data-path="/"><span>${getIcon('layout-dashboard', 20)}</span><span>Dashboard</span></a>
    <button type="button" data-act="find"><span>${getIcon('search', 20)}</span><span>Find</span></button>
    ${writer ? `<button type="button" class="sc-bottom-log" data-act="log"><span>${getIcon('plus', 20)}</span><span>Log</span></button>`
      : `<a href="/customers" data-link data-path="/customers"><span>${getIcon('users', 20)}</span><span>Accounts</span></a>`}
    <a href="/pipeline" data-link data-path="/pipeline"><span>${getIcon('git-merge', 20)}</span><span>Pipeline</span></a>
    <button type="button" data-act="more"><span>${getIcon('menu', 20)}</span><span>More</span></button>`;
  bar.addEventListener('click', (e) => {
    const act = e.target.closest('[data-act]')?.dataset.act;
    if (act === 'find') openFind();
    else if (act === 'log') openLogSheet(currentAccount());
    else if (act === 'more') document.querySelector('.mobile-menu-btn')?.click();
  });
  const mark = () => bar.querySelectorAll('[data-path]').forEach(a => {
    const p = a.dataset.path;
    a.classList.toggle('active', p === '/' ? window.location.pathname === '/' : window.location.pathname.startsWith(p));
  });
  router.after(mark);
  mark();
  document.body.classList.add('sc-has-bottom-bar');
  return bar;
}
